import React from "react";

/**
 * Phase 1 — Card
 *
 * Single unified card primitive with 16px corners and soft elevation.
 *
 *   <Card variant="raised">  → default, soft shadow, sits above paper bg
 *   <Card variant="flat">    → no shadow, hairline divider top/bottom
 *                             (use for dense list groups)
 *   <Card variant="pressed"> → paper-sunk bg, inset shadow
 *                             (use for selected/active states)
 *
 *   <Card interactive>       → adds press animation (scale 0.98)
 *
 * Permanent rule: this is the only card primitive going forward.
 * Legacy `.card-flat` utility class remains in `index.css` for pages
 * not yet migrated; do not delete it until Phases 2 & 3 finish.
 */
const VARIANTS = {
  raised:  "bg-paper-2 shadow-rest",
  flat:    "bg-paper-2 border-y border-border/60",
  pressed: "bg-paper-sunk shadow-press",
};

export default function Card({
  variant = "raised",
  interactive = false,
  className = "",
  children,
  ...rest
}) {
  const variantClass = VARIANTS[variant] || VARIANTS.raised;
  const interactClass = interactive
    ? "transition-all duration-150 ease-out cursor-pointer hover:shadow-raised active:scale-[0.98] active:shadow-press"
    : "";
  // Flat variant deliberately uses zero radius so adjacent flat cards
  // form a continuous list. Raised + pressed take the full 16px.
  const radiusClass = variant === "flat" ? "" : "rounded-card";
  return (
    <div
      className={`${variantClass} ${radiusClass} ${interactClass} ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}
